<template>
  <div class="max-w-3xl" v-if="page">
    <div class="adthrive-header min-h-[280px] overflow-hidden"></div>
    <Ad mb-4 ad-slot="6966721074" />
    <h1 class="font-slab text-2xl text-brand-blue">{{ placeName ? `${placeName} ${page.title}` : page.title }}</h1>
    <LocationProblem v-if="locationError" :error="locationError" subject="snow totals near you" my-4 />
    <LocationLookup v-model="selectedLocation" />
    <div space-y-4>
      <div v-if="snowForecast">
        <h2 text-2xl font-semibold text-stone-500>48 hour Snowfall Forecast</h2>
        <div text-3xl font-semibold text-stone-700 tabular-nums>{{ snowForecast }}</div>
        <div font-mono text text-stone-500 lh-snug></div>
      </div>

      <h2 text-2xl font-semibold text-stone-500>Snowfall Map</h2>
      <LazyMapSnowfall :location="coordinates" @markerMove="setMapLocation" />
      <p prose lg:text-lg>
        View snow accumulations over the most recent period of 6 hours, 24 hours, 48 hours (2 days), and 72 hours (3
        days) provided by the the National Water Center
        <a href="https://www.nohrsc.noaa.gov/snowfall_v2/">National Gridded Snowfall Analysis</a>. The data is a
        combination of snowfall reports, high-resolution weather models, precipitation estimates, and temperature data
        to create accurate snowfall maps. You can drag the marker to see the snowfall reading at that location.
      </p>

      <div flex flex-col md:flex-row gap-4 justify-between pt-6>
        <h2 text-2xl font-semibold text-stone-500>Station Readings</h2>
        <div class="md:(border-0 p-0) border-3 border-stone-100 rounded px-4 py-2 flex gap-4 items-center">
          <label for="date" class="font-semibold text-stone-500 md:sr-only">Date</label>
          <DateSelect id="date" v-model="date" />
        </div>
      </div>

      <div w-full h-lg bg-stone-50>
        <LazyMapSnowReadings :readings="snow?.data || []" :location="coordinates" @markerMove="setMapLocation" />
      </div>

      <div grid grid-flow-dense sm:grid-cols-2 gap-2 relative>
        <template v-for="reading in readings" :key="reading?.station_id">
          <SnowReading :reading="reading" :loading="snow?.pending || loading" />
        </template>
      </div>
      <div v-if="(snow?.data?.length > 4 || loading) && !showAllReadings" class="text-center">
        <Button :disabled="loading" text-lg color="blue" @click="showAllReadings = true">Show more</Button>
      </div>

      <p prose lg:text-lg>
        The station readings on this site show the nearest snow observations reported by the National Weather Service
        <a href="https://www.nohrsc.noaa.gov">National Operational Hydrologic Remote Sensing Center</a> based on the
        date selected. New snow total and snow depth readings are updated throughout the day, typically within 24 hours
        of the observation.
      </p>

      <p v-if="coordinates && readings.length === 0 && !loading">
        No snowfall readings were found for this location and date.
      </p>

      <div space-y-4 v-if="snow && selectedLocation?.module === 'currentLocation'">
        <div text-stone-5 prose lg:text-lg v-if="isLocationSupported">
          <p v-if="!loading && snow?.error && !isBot()">An error occurred. Please try again later.</p>
          <p v-else-if="!loading && !snow?.data && !isBot()">
            We could not determine the {{ page.label }} for your location.
          </p>
        </div>
      </div>

      <h2 text-2xl font-semibold text-stone-500>Current Snow Depth</h2>
      <LazyMapSnowDepth :location="coordinates" @markerMove="setMapLocation" />
      <p prose lg:text-lg>
        View the current snow depth at any location in the United States. Drag the marker to see the snow depth at any
        location. Snow depth data is updated daily with data provided by <a href="https://nsidc.org/home">SNODAS</a>.
      </p>

      <h2 text-2xl font-semibold text-stone-500>Seasonal Snowfall Map</h2>
      <LazyMapSnowfallSeason :location="coordinates" @markerMove="setMapLocation" />
      <p prose lg:text-lg>
        View the sum of all snowfall over the current season, starting September 30th. Seasonal snow accumulations are
        updated every 12 hours with data provided by the the
        <a href="https://www.nohrsc.noaa.gov/snowfall_v2/">National Gridded Snowfall Analysis</a>. Drag the marker to
        see the snowfall total at any location in the United States.
      </p>

      <div space-y-4>
        <h2 text-xl sm:text-2xl font-semibold text-stone-500>{{ countyType }} Snowfall Records</h2>
        <h3 text-xl font-semibold text-stone-400 v-if="snowExtremes?.pending || loading || snowExtremes?.data?.county">
          <template v-if="snowExtremes?.pending || loading">&nbsp;</template>
          <template v-else> {{ snowExtremes?.data?.countyName }}, {{ snowExtremes?.data?.state }} </template>
        </h3>
        <div grid sm:grid-cols-3 gap-2>
          <template v-for="reading in snowExtremes?.data?.readings" :key="reading.station_id">
            <SnowReading :reading="reading" />
          </template>
          <template v-if="loading || snowExtremes?.pending">
            <SnowReading
              v-for="_ in [].constructor(3)"
              :loading="true"
              :class="{ invisible: snowExtremes?.data?.readings?.length == 0 }"
            />
          </template>
        </div>
        <template v-if="coordinates">
          <p prose lg:text-lg v-if="!loading && !snowExtremes?.pending && !snowExtremes?.data?.description">
            No snowfall records were found for
            <template v-if="snowExtremes?.data?.county"
              >{{ snowExtremes?.data?.countyName }}, {{ snowExtremes?.data?.state }}</template
            ><template v-else>this {{ countyType.toLowerCase() }}</template>.
          </p>
          <p prose lg:text-lg v-else>
            The highest recorded snow totals for your {{ countyType.toLowerCase() }} were observed at the stations above over a one day, two day,
            and three day period.
          </p>
        </template>
        <p prose lg:text-lg>{{ snowExtremes?.data?.description }}</p>
      </div>

      <Ad ad-slot="1948746426" />
      <WikipediaInfo v-if="placeName" :slug="placeName" />
    </div>

    <div class="prose lg:text-lg">
      <h2 class="text-3xl font-semibold text-stone-500">{{ page?.subtitle || "What is this?" }}</h2>
      <ContentRenderer v-if="page" :value="page" />
    </div>
  </div>
</template>
<script setup>
const page = await useContent("/snow");

const { selectedLocation, coordinates, placeName, locationError, setMapLocation, onCoordinatesChange, geolocation } =
  useSelectedLocation();
const { isLocationSupported } = geolocation;

// Location URLs from the atlas are the pages that rank, so name the place in the title,
// heading, and description.
const description = computed(() => {
  if (placeName.value) {
    return `How much snow did ${placeName.value} get? See snowfall for the last 24, 48, and 72 hours, nearby station reports, snow depth, and the season total.`;
  }
  return page.value.description;
});

useSeoMeta({
  title: () => {
    if (placeName.value) {
      return `${placeName.value} ${page.value.title} - Recent Snowfall`;
    }
    return `${page.value.title} - ${page.value.subtitle}`;
  },
  description,
  ogDescription: description,
});

defineOgImage({
  title: page.value.title,
  subtitle: page.value.subtitle,
});

const date = ref(toISODate(new Date()));
const snow = ref();
const snowExtremes = ref();
// "County" unless the records are for e.g. a Louisiana parish or an Alaska borough
const countyType = computed(() => snowExtremes.value?.data?.countyType || "County");
const forecast = ref();
const showAllReadings = ref(false);

const readings = computed(() => {
  if (snow.value?.pending === true || loading.value) {
    return [].constructor(4);
  }
  if (!snow.value?.data?.length) {
    return [];
  }
  const readingsWithData = snow.value.data.filter(
    (reading) => isNumber(reading?.snowfall_inches) || isNumber(reading?.snow_depth_inches)
  );
  return showAllReadings.value ? readingsWithData : readingsWithData.slice(0, 4);
});

const loading = ref(true);

async function update() {
  if (!coordinates.value) {
    return;
  }
  loading.value = true;
  snow.value = null;
  snowExtremes.value = null;
  forecast.value = null;
  try {
    // the date is a little messed up with timezones, and sometimes setting today doesn't work
    // for now, just don't send the date if it's today
    const requestDate = date.value == toISODate(new Date()) ? null : date.value;
    snow.value = await useFetchSnow(coordinates.value, requestDate);
    snowExtremes.value = await useFetchSnowExtremes(coordinates.value);
    if (!date.value) {
      // TODO: reenable once UI is straightened out
      // only fetch forecast if date is not set (default to current day)
      // forecast.value = await useFetchForecast(coordinates.value, "snow");
    }
  } catch (error) {
    console.error(error);
  }
  loading.value = false;
}

onCoordinatesChange(update);
// there is nothing to load until a location is chosen
watch(locationError, (error) => {
  if (error) {
    loading.value = false;
  }
});
watch(date, update);

const snowForecast = computed(() => {
  const forecastData = forecast.value?.data;
  if (!isNumber(forecastData?.low48) || !isNumber(forecastData?.high48)) {
    return null;
  }
  if (forecastData.low48 == forecastData.high48) {
    return `${forecastData.low48}\u00A0in`;
  }
  return `${forecastData.low48}–${forecastData.high48}\u00A0in`;
});
</script>
